import { yupResolver } from "@hookform/resolvers/yup";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  TextField,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import ErrorWarningIcon from "components/common/InputErrorWarningIcon";
import { EPath } from "constants/path";
import { SessionStorageKeys } from "constants/secureStorage";
import { useToast } from "contexts/ToastContext";
import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router";

import { useAuth } from "../../../contexts/AuthContext";
import { GoogleSignInRequestUserTypeEnum } from "../../../libs/cellera-sdk";
import { useCelleraTheme } from "../../../theme/useCelleraTheme";
import { AuthResult } from "../../../types";
import { buttonVariants, layoutStyles } from "../../../utils/muiTailwindStyles";
import { signInSchema } from "../../../validations";

interface SignInFormData {
  email: string;
  password: string;
}

interface LoginEmailPasswordFormProps {
  userType: GoogleSignInRequestUserTypeEnum;
  onSuccess: (result: AuthResult) => void;
  onError: (error: Error) => void;
  rememberMe: boolean;
  setRememberMe: (value: boolean) => void;
}

const LoginEmailPasswordForm: React.FC<LoginEmailPasswordFormProps> = ({
  userType,
  onSuccess,
  onError,
  rememberMe,
  setRememberMe,
}) => {
  const navigate = useNavigate();
  const { colors } = useCelleraTheme();
  const { showSuccess, showWarning, showError } = useToast();
  const { signInWithEmail } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const signInForm = useForm<SignInFormData>({
    resolver: yupResolver(signInSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      email: "",
      password: "",
    },
  });

  // Check for stored email from sign-up and pre-fill the form
  useEffect(() => {
    const storedEmail = sessionStorage.getItem(
      SessionStorageKeys.SIGN_UP_EMAIL,
    );
    if (storedEmail) {
      signInForm.setValue("email", storedEmail);
    }
  }, [signInForm]);

  const watchedEmail = signInForm.watch("email");
  useEffect(() => {
    const storedEmail = sessionStorage.getItem(
      SessionStorageKeys.SIGN_UP_EMAIL,
    );
    if (storedEmail && watchedEmail && watchedEmail !== storedEmail) {
      // User has manually changed the email, clear the stored email
      sessionStorage.removeItem(SessionStorageKeys.SIGN_UP_EMAIL);
    }
  }, [watchedEmail]);

  const handleSubmit = async (data: SignInFormData) => {
    try {
      setIsLoading(true);

      const { email, password } = data;

      const result = await signInWithEmail(
        email,
        password,
        userType,
        rememberMe,
      );

      // Check if email verification is required
      if (
        "requiresEmailVerification" in result &&
        result.requiresEmailVerification
      ) {
        showWarning(
          "Email verification required",
          "Please check your email to verify your account before logging in.",
        );
        onSuccess({ ...result, email });
      } else {
        showSuccess("Login successful!", "Welcome back to your dashboard.");
        sessionStorage.removeItem(SessionStorageKeys.SIGN_UP_EMAIL);
        onSuccess(result);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Login failed. Please try again.";
      showError("Login failed", errorMessage);
      onError(error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSwitchForm = () => {
    signInForm.reset();
    navigate(EPath.FORGOT_PASSWORD);
  };

  return (
    <Box className={layoutStyles.form}>
      <Box
        component="form"
        onSubmit={signInForm.handleSubmit(handleSubmit)}
        className="flex flex-col space-y-4"
      >
        <Controller
          name="email"
          control={signInForm.control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              fullWidth
              label="Email"
              type="email"
              error={!!error}
              helperText={error?.message}
              slotProps={{
                input: {
                  endAdornment: error && (
                    <ErrorWarningIcon hasError={!!error} />
                  ),
                },
                htmlInput: {
                  maxLength: 255,
                },
              }}
            />
          )}
        />

        <Controller
          name="password"
          control={signInForm.control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              fullWidth
              label="Password"
              type={showPassword ? "text" : "password"}
              error={!!error}
              helperText={error?.message}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      {!!error && <ErrorWarningIcon hasError={!!error} />}
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: colors.text.secondary }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
                htmlInput: {
                  maxLength: 255,
                },
              }}
            />
          )}
        />

        {/* Forgot Password & Remember Me */}
        <Box className="flex justify-between items-center">
          <FormControlLabel
            control={
              <Checkbox
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                sx={{
                  color: colors.primary.main,
                  "&.Mui-checked": {
                    color: colors.primary.main,
                  },
                }}
              />
            }
            label={
              <Typography sx={{ color: colors.text.secondary }}>
                Remember me
              </Typography>
            }
          />
          <Button
            variant="text"
            onClick={handleSwitchForm}
            className="transition-colors"
            sx={{
              textTransform: "none",
              color: colors.primary.main,
              "&:hover": {
                color: colors.primary.dark,
                backgroundColor: colors.primary.light,
              },
              fontWeight: 500,
            }}
          >
            Forgot password?
          </Button>
        </Box>

        <Button
          type="submit"
          variant="contained"
          disabled={isLoading || !signInForm.formState.isValid}
          className={`${buttonVariants.primary.className} rounded-full py-3`}
          sx={{
            ...buttonVariants.primary.sx,
            width: "100%",
            fontSize: "16px",
            "&:disabled": {
              backgroundColor: colors.border.main,
              color: colors.text.disabled,
            },
          }}
        >
          {isLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Log In"
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default LoginEmailPasswordForm;
